import { computed, ref, type Ref, type ComputedRef } from 'vue'
import { useNuxtApp } from '#app'
import { useEditModel } from '@/composables/useEditModel'
import type { BaseService } from '@/_api/services/base'
import type { BaseModel } from '@/_api/models/base'

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Configuration for the primary model (uses useEditModel under the hood).
 */
export interface PrimaryModelConfig<T extends BaseModel> {
  id: string
  service: BaseService<T>
  immediate?: boolean
  successNotification?: boolean
  onBeforeLoad?: () => void
  onAfterLoad?: (data: T) => void
  onLoadError?: (error: any) => void
  onBeforeSubmit?: (data: T) => void
  onAfterSubmit?: (response: any, data: T) => void
  onSubmitError?: (error: any, data: T) => void
}

/**
 * Configuration for a secondary model with custom fetch/save logic.
 */
export interface SecondaryModelConfig<TSecondary, TPrimary extends BaseModel> {
  /** Custom fetch function. Receives the primary model data after it loads. */
  fetch: (primary: TPrimary) => Promise<TSecondary>
  /** Custom save function. Only called if the secondary data has changed. */
  save: (data: TSecondary, primary: TPrimary) => Promise<void>
  /** Optional: called after a successful save */
  onAfterSave?: (data: TSecondary, primary: TPrimary) => void
  /** Optional: called on save error */
  onSaveError?: (error: any, data: TSecondary) => void
  /**
   * Whether this secondary model should only load for existing records
   * (i.e., skip when primary id === 'new'). Defaults to true.
   */
  existingOnly?: boolean
}

/**
 * Reactive state for a single secondary model.
 */
export interface SecondaryModelState<T> {
  data: Ref<T | null>
  changed: ComputedRef<boolean>
  loading: Ref<boolean>
}

/**
 * Maps secondary config keys to their reactive state types.
 */
type SecondaryModelsMap = Record<string, SecondaryModelConfig<any, any>>

type SecondaryModelsState<TMap extends SecondaryModelsMap> = {
  [K in keyof TMap]: TMap[K] extends SecondaryModelConfig<infer S, any>
    ? SecondaryModelState<S>
    : never
}

// ─── Options & Return ────────────────────────────────────────────────────────

export interface UseEditModelsOptions<
  TPrimary extends BaseModel,
  TSecondary extends SecondaryModelsMap = {}
> {
  primary: PrimaryModelConfig<TPrimary>
  secondary?: TSecondary
}

export interface UseEditModelsReturn<
  TPrimary extends BaseModel,
  TSecondary extends SecondaryModelsMap = {}
> {
  /** The primary model's reactive data */
  data: Ref<TPrimary | undefined>
  /** Secondary model states, keyed by config name */
  models: SecondaryModelsState<TSecondary>
  /** True while the primary model is loading */
  loading: Ref<boolean>
  /** True while any model is currently being saved */
  saving: Ref<boolean>
  /** True if ANY model (primary or secondary) has unsaved changes */
  changed: ComputedRef<boolean>
  /** Save all changed models (primary first, then secondaries) */
  onSubmit: () => Promise<void>
  /** Re-fetch all models */
  fetchData: () => Promise<void>
  /** All reactive refs for passing to usePanel's watch parameter */
  watchRefs: Ref<any>[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Serialise a value to a JSON string for change detection.
 * Uses toJSON() if available (BaseModel instances), otherwise JSON.stringify directly.
 */
function snapshot(value: any): string {
  if (value && typeof value === 'object' && typeof value.toJSON === 'function') {
    return JSON.stringify(value.toJSON())
  }
  return JSON.stringify(value)
}

// ─── Composable ──────────────────────────────────────────────────────────────

export const useEditModels = <
  TPrimary extends BaseModel,
  TSecondary extends SecondaryModelsMap = {}
>(
  options: UseEditModelsOptions<TPrimary, TSecondary>
): UseEditModelsReturn<TPrimary, TSecondary> => {
  const { $notification } = useNuxtApp()

  // Track whether the primary save succeeded (needed because useEditModel
  // catches errors internally and doesn't re-throw)
  let primarySaveSucceeded = false

  // ── Secondary state setup ──────────────────────────────────────────────

  const secondaryEntries = Object.entries(options.secondary || {}) as [
    string,
    SecondaryModelConfig<any, TPrimary>
  ][]

  // Internal original snapshots (not exposed publicly)
  const originals = new Map<string, Ref<string | null>>()

  const secondaryStates: Record<string, SecondaryModelState<any>> = {}

  for (const [key] of secondaryEntries) {
    const data = ref<any>(null)
    const originalJSON = ref<string | null>(null)
    const loading = ref(false)

    originals.set(key, originalJSON)

    const changed = computed<boolean>(() => {
      if (!data.value || !originalJSON.value) return false
      return snapshot(data.value) !== originalJSON.value
    })

    secondaryStates[key] = { data, changed, loading }
  }

  // ── Fetch secondaries (called after primary loads) ─────────────────────

  const fetchSecondaries = async (primary: TPrimary): Promise<void> => {
    const isNewRecord = options.primary.id === 'new'

    await Promise.all(
      secondaryEntries.map(async ([key, config]) => {
        // Skip if existingOnly (default) and this is a new record
        if (config.existingOnly !== false && isNewRecord) return

        const state = secondaryStates[key]
        const originalJSON = originals.get(key)!

        try {
          state.loading.value = true
          const result = await config.fetch(primary)
          state.data.value = result
          originalJSON.value = snapshot(result)
        } catch (error) {
          console.error(`Failed to load secondary model "${key}":`, error)
          $notification.error({ errors: (error as any)?.errors })
        } finally {
          state.loading.value = false
        }
      })
    )
  }

  // ── Primary model via useEditModel ─────────────────────────────────────

  // Wrap the consumer's onAfterLoad to chain secondary fetches
  const consumerOnAfterLoad = options.primary.onAfterLoad
  const wrappedOnAfterLoad = async (data: TPrimary): Promise<void> => {
    if (consumerOnAfterLoad) {
      await consumerOnAfterLoad(data)
    }
    await fetchSecondaries(data)
  }

  // Wrap onAfterSubmit and onSubmitError to track primary save success
  const consumerOnAfterSubmit = options.primary.onAfterSubmit
  const wrappedOnAfterSubmit = (response: any, data: TPrimary): void => {
    primarySaveSucceeded = true
    if (consumerOnAfterSubmit) {
      consumerOnAfterSubmit(response, data)
    }
  }

  const consumerOnSubmitError = options.primary.onSubmitError
  const wrappedOnSubmitError = (error: any, data: TPrimary): void => {
    primarySaveSucceeded = false
    if (consumerOnSubmitError) {
      consumerOnSubmitError(error, data)
    }
  }

  const {
    data,
    saving: primarySaving,
    loading,
    changed: primaryChanged,
    onSubmit: primarySubmit,
    fetchData: primaryFetchData,
  } = useEditModel<TPrimary>({
    ...options.primary,
    onAfterLoad: wrappedOnAfterLoad,
    onAfterSubmit: wrappedOnAfterSubmit,
    onSubmitError: wrappedOnSubmitError,
  })

  // ── Combined change detection ──────────────────────────────────────────

  const changed = computed<boolean>(() => {
    if (primaryChanged.value) return true
    return Object.values(secondaryStates).some(
      (state: SecondaryModelState<any>) => state.changed.value
    )
  })

  // ── Combined saving state ──────────────────────────────────────────────

  const secondarySaving = ref(false)

  const saving = computed<boolean>(() => {
    return primarySaving.value || secondarySaving.value
  })

  // ── Combined submit ────────────────────────────────────────────────────

  const onSubmit = async (): Promise<void> => {
    // Save primary first
    primarySaveSucceeded = false
    await primarySubmit()

    // Only save secondaries if primary succeeded
    if (!primarySaveSucceeded) return

    secondarySaving.value = true

    try {
      for (const [key, config] of secondaryEntries) {
        const state = secondaryStates[key]
        const originalJSON = originals.get(key)!

        // Skip if not changed
        if (!state.changed.value) continue
        if (!state.data.value || !data.value) continue

        try {
          await config.save(state.data.value, data.value as TPrimary)

          // Update original snapshot after successful save
          originalJSON.value = snapshot(state.data.value)

          if (config.onAfterSave) {
            config.onAfterSave(state.data.value, data.value as TPrimary)
          }
        } catch (error) {
          if (config.onSaveError) {
            config.onSaveError(error, state.data.value)
          } else {
            console.error(`Failed to save secondary model "${key}":`, error)
            $notification.error({ errors: (error as any)?.errors })
          }
          throw error // Stop sequential chain
        }
      }
    } finally {
      secondarySaving.value = false
    }
  }

  // ── Combined fetch ─────────────────────────────────────────────────────

  const fetchData = async (): Promise<void> => {
    // Secondaries are automatically fetched via the wrapped onAfterLoad
    await primaryFetchData()
  }

  // ── watchRefs for usePanel ─────────────────────────────────────────────

  const watchRefs: Ref<any>[] = [
    data,
    ...Object.values(secondaryStates).map(
      (state: SecondaryModelState<any>) => state.data
    ),
  ]

  // ── Return ─────────────────────────────────────────────────────────────

  return {
    data,
    models: secondaryStates as SecondaryModelsState<TSecondary>,
    loading,
    saving: saving as unknown as Ref<boolean>,
    changed,
    onSubmit,
    fetchData,
    watchRefs,
  }
}
